import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { dramas } from "@/mocks/dramas";
import { useWatchHistory } from "@/hooks/useWatchHistory";
import { useSeo } from "@/hooks/useSeo";
import { categoryLabel, episodeLabel } from "@/utils/drama";

export default function History() {
  const { entries, clearHistory } = useWatchHistory();

  useSeo({
    title: "我的播放历史 - 继续追剧 | 剧焰短剧",
    description: "查看你在剧焰短剧的播放历史，快速回到上次观看的剧集与集数，支持一键清空记录，继续未看完的短剧。",
    keywords: "播放历史,继续观看,短剧追剧记录,剧焰短剧",
    path: "/history",
  });

  const items = entries
    .map((entry) => {
      const drama = dramas.find((item) => item.id === entry.id);
      return drama ? { drama, episode: entry.episode, watchedAt: entry.watchedAt } : null;
    })
    .filter((item): item is { drama: (typeof dramas)[number]; episode: number; watchedAt: number } => item !== null);

  return (
    <>
      <SiteHeader />
      <main className="w-full px-4 pb-6 md:px-6">
        <nav aria-label="面包屑导航" className="flex items-center gap-1.5 pt-5 text-xs text-foreground-500 md:text-sm">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-300">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-700" aria-hidden="true" />
          <span className="text-foreground-200">播放历史</span>
        </nav>

        <header className="mt-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-50 md:text-3xl">播放历史</h1>
            <p className="mt-2 text-xs text-foreground-500 md:text-sm">
              记录保存在你的浏览器本地，共 {items.length} 部剧集，可随时继续观看
            </p>
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={clearHistory}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-foreground-50/15 px-4 py-2 text-xs text-foreground-300 transition-colors hover:border-accent-400/60 hover:text-accent-300 md:text-sm"
            >
              <i className="ri-delete-bin-6-line text-sm" aria-hidden="true" />
              清空历史
            </button>
          )}
        </header>

        {items.length > 0 ? (
          <ul className="mt-6 grid grid-cols-1 gap-3 md:mt-8 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
            {items.map(({ drama, episode }) => (
              <li key={drama.id}>
                <Link
                  to={`/drama/${drama.id}?ep=${episode}`}
                  className="group flex cursor-pointer gap-3 rounded-xl border border-foreground-50/10 bg-foreground-50/[0.025] p-3 transition-colors hover:border-primary-400/50"
                >
                  <img
                    src={drama.cover}
                    alt={`《${drama.title}》封面`}
                    width={600}
                    height={800}
                    loading="lazy"
                    className="h-[104px] w-[78px] shrink-0 rounded-lg object-cover object-top"
                  />
                  <div className="flex min-w-0 flex-col justify-between py-0.5">
                    <div>
                      <h2 className="line-2 text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-300">
                        {drama.title}
                      </h2>
                      <p className="mt-1 text-[11px] text-foreground-500">
                        {categoryLabel(drama.category)} · {episodeLabel(drama)}
                      </p>
                    </div>
                    <p className="flex items-center gap-1.5 text-[11px] text-primary-300">
                      <i className="ri-play-circle-line text-sm" aria-hidden="true" />
                      继续观看第 {episode} 集
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-foreground-50/15 bg-foreground-50/[0.02] px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-foreground-50/[0.06] text-foreground-500">
              <i className="ri-history-line text-2xl" aria-hidden="true" />
            </span>
            <p className="mt-4 text-sm text-foreground-200">还没有播放记录</p>
            <p className="mt-1 text-xs text-foreground-500">随便挑一部热门短剧开始追剧吧</p>
            <Link
              to="/"
              className="mt-5 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-xs font-bold text-background-950 transition-colors hover:bg-primary-400"
            >
              去首页看看
            </Link>
          </div>
        )}
      </main>
      <SiteFooter />
    </>
  );
}